import React, { useEffect, useState } from "react";

// STYLES
import * as Style from "./NotificationsStyle";

// LIBRARIES
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

// MISC
import { COLORS } from "constants/styleVariables";
import { IProps } from "./NotificationsModel";
import { NotificationType } from "models/GeneralTypes/generalTypes";
import { IUserDetails } from "models/interfaces";
import {
  INotificationDetails,
  INotificationDetailsContent,
} from "models/interfaces";

// ASSETS
import PaymentIcon from "assets/Icons/CalendarIcon.svg";
import ClockIcon from "assets/Icons/ClockIcon.svg";
import PeoplesIcon from "assets/Icons/PeoplesIcon.svg";
import GrayPaymentIcon from "assets/Icons/GrayCalendarIcon.svg";
import GrayClockIcon from "assets/Icons/GrayClockIcon.svg";
import GrayPeoplesIcon from "assets/Icons/GrayPeoplesIcon.svg";

// REDUX
import { useAppDispatch } from "hooks/reduxHooks";
import {
  getNotifications,
  markNotificationsAsRead,
} from "api/notificationsApi";
import moment from "moment";

// COMPONENTS
const Notifications = (props: IProps) => {
  // PROPS
  const { handleClose = () => { } } = props;

  // CONSTANTS USING LIBRARIES
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // CONSTANTS USING HOOKS
  const [notificationDetails, setNotificationDetails] =
    useState<INotificationDetails>({
      content: [],
      pageable: null,
      totalPages: 0,
      totalElements: 0,
      last: false,
      size: 0,
      number: 0,
      sort: null,
      numberOfElements: 0,
      first: false,
      empty: true,
    });
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // GENERAL CONSTANTS
  const allRead = notificationDetails?.content.every(
    (notification) => notification.isRead
  );
  const currentDate = new Date();

  // USE EFFECT FUNCTION
  useEffect(() => {
    getNotificationsData(0)
    // eslint-disable-next-line
  }, []);
  // REQUEST API FUNCTIONS

  // HANDLERS FUNCTIONS
  const handleScrollEnd = (e: React.UIEvent<HTMLDivElement>) => {
    const divElement = e.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = divElement;
    if (scrollTop + clientHeight === scrollHeight) {
      getNotificationsData(currentPage);
    }
  };
  const renderIcon = (type: string, readState: boolean) => {
    switch (type) {
      case NotificationType.RENTAL_STARTED:
        return readState ? GrayClockIcon : ClockIcon;
      case NotificationType.PAYMENT:
        return readState ? GrayPaymentIcon : PaymentIcon;
      case NotificationType.USER_REGISTER:
        return readState ? GrayPeoplesIcon : PeoplesIcon;
      case NotificationType.RENTAL_ENDED:
        return readState ? GrayClockIcon : ClockIcon;
    }
  };

  const renderNotificationText = (
    type: string,
    notificationUserDetails: IUserDetails
  ) => {
    switch (type) {
      case NotificationType.RENTAL_STARTED:
        return t("modal.notifications.rentalStartedText", {
          fullName:
            notificationUserDetails.fullName !== ""
              ? notificationUserDetails.fullName
              : notificationUserDetails.username,
          createdAt: dayjs(notificationUserDetails.createdAt).format(
            "hh:mm:ss"
          ),
        });
      case NotificationType.RENTAL_ENDED:
        return t("modal.notifications.rentalEndedText", {
          fullName:
            notificationUserDetails.fullName !== ""
              ? notificationUserDetails.fullName
              : notificationUserDetails.username,
          createdAt: dayjs(notificationUserDetails.createdAt).format(
            "hh:mm:ss"
          ),
        });
      case NotificationType.USER_REGISTER:
        return t("modal.notifications.userRegisterText", {
          fullName:
            notificationUserDetails.fullName !== ""
              ? notificationUserDetails.fullName
              : notificationUserDetails.username,
          createdAt: dayjs(notificationUserDetails.createdAt).format(
            "hh:mm:ss"
          ),
        });
      case NotificationType.PAYMENT:
        return t("modal.notifications.paymentText", {
          fullName:
            notificationUserDetails.fullName !== ""
              ? notificationUserDetails.fullName
              : notificationUserDetails.username,
          createdAt: dayjs(notificationUserDetails.createdAt).format(
            "hh:mm:ss"
          ),
        });
    }
  };
  const renderNotifications = (
    notificationDetails: INotificationDetailsContent
  ) => {
    const date = notificationDetails.creationDate;
    return (
      <>
        <Style.TextIcon
          src={renderIcon(notificationDetails.type, notificationDetails.isRead)}
        />
        <Style.Text
          color={notificationDetails.isRead === true && COLORS.primaryGray}
        >
          {renderNotificationText(
            notificationDetails.type,
            notificationDetails.user
          )}
          {moment(date).format('DD/MM/YYYY') !== moment(currentDate).format('DD/MM/YYYY') && ` -  ${moment(date).format('DD/MM/YYYY')}`}
        </Style.Text>
      </>
    );
  };

  const getNotificationsData = (pageNumber) => {
    if (pageNumber === 0) {
      setIsLoading(true);
    }
    dispatch(getNotifications(pageNumber))
      .unwrap()
      .then((response) => {
        setNotificationDetails({
          ...response,
          content: [...notificationDetails?.content, ...response.content],
        });
        const unreadIds = notificationDetails?.content
          .filter((notification) => !notification.isRead)
          .map((notification) => notification.id);
        const idsString = unreadIds.join(",");
        dispatch(markNotificationsAsRead(idsString));
        setCurrentPage(pageNumber + 1);
        setIsLoading(false);
      });
  };

  return (
    <Style.NotificationContainer>
      <Style.CloseIconContainer>
        <Style.CloseIcon onClick={handleClose} />
      </Style.CloseIconContainer>
      <Style.TitleContainer>
        <Style.Title>{t("modal.notifications.title")}</Style.Title>
      </Style.TitleContainer>
      {isLoading ?
        <Style.LoadingContainer>
          <Style.LoadingSign />
        </Style.LoadingContainer>
        :
        <Style.NotificationContentContainer onScroll={handleScrollEnd}>
          {!allRead && (
            <Style.SubtitleContainer>
              <Style.Subtitle>{t("modal.notifications.unread")}</Style.Subtitle>
            </Style.SubtitleContainer>
          )}
          {notificationDetails !== null &&
            notificationDetails.content.map((notification) => {
              return (
                <>
                  {!notification.isRead && (
                    <>
                      <Style.TextContainer>
                        {renderNotifications(notification)}
                      </Style.TextContainer>
                      <Style.Bar />
                    </>
                  )}
                </>
              );
            })}
          <Style.SubtitleContainer>
            <Style.Subtitle>{t("modal.notifications.read")}</Style.Subtitle>
          </Style.SubtitleContainer>
          {notificationDetails !== null &&
            notificationDetails.content.map((notification) => {
              return (
                <>
                  {notification.isRead && (
                    <>
                      <Style.TextContainer>
                        {renderNotifications(notification)}
                      </Style.TextContainer>
                      <Style.Bar />
                    </>
                  )}
                </>
              );
            })}
        </Style.NotificationContentContainer>}
      {/* <Style.HistoryNotificationText>
        Istoric notificari
      </Style.HistoryNotificationText> */}
    </Style.NotificationContainer>
  );
};

export default Notifications;
